import { useState } from 'react';
import { createRoute, Link, useParams } from '@tanstack/react-router';
import {
  IconChevronLeft,
  IconExternalLink,
  IconFileText,
  IconListCheck,
  IconShieldCheck,
  IconFlag,
  IconMapPin,
  IconStar,
  IconStarFilled,
} from '@tabler/icons-react';
import { Route as appRoute } from '../../app.js';
import { useCourse } from '../../../hooks/useCourses.js';
import { useShortlistIds, useToggleShortlist } from '../../../hooks/useShortlist.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { CourseStatStrip } from '../../../components/courses/CourseStatStrip.js';
import {
  AgencyOverlayEditor,
  AgencyNotesPanel,
} from '../../../components/courses/AgencyOverlay.js';
import { Button } from '../../../components/ui/Button.js';
import { centsToAud, formatDate } from '../../../lib/format.js';
import { SendOverviewModal, SendChecklistModal } from '../../../components/send/index.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/courses/$category/$subCategory/$courseId',
  component: CourseDetailPage,
});

function CourseDetailPage() {
  const { category, subCategory, courseId } = useParams({
    from: '/app/courses/$category/$subCategory/$courseId',
  });
  const { data, isLoading, error } = useCourse(courseId);
  const { user } = useAuth();
  const shortlistIds = useShortlistIds();
  const toggleShortlist = useToggleShortlist();
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);

  if (isLoading) {
    return <div className="text-muted">Loading course…</div>;
  }
  if (error || !data?.course) {
    return (
      <div className="surface p-6 text-danger text-sm">
        Couldn't load this course. It may have been removed.
      </div>
    );
  }

  const c = data.course;
  // The badge means a person checked this course — only the recorded
  // verification stamp can show it (never curation state).
  const isVerified = c.verifiedAt != null;
  const isPr = c.prRelevant;
  const saved = shortlistIds.has(c.id);
  const lifetimeFee =
    c.totalProgramCents !== null ? centsToAud(c.totalProgramCents) : null;

  return (
    <div className="max-w-5xl pb-24 lg:pb-0">
      {/* Breadcrumb */}
      <Link
        to="/app/courses/$category/$subCategory"
        params={{ category, subCategory }}
        className="inline-flex items-center gap-1 text-xs text-muted hover:text-navy transition mb-4"
      >
        <IconChevronLeft size={14} />
        Back to courses
      </Link>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-navy">
            {c.providerName}
          </span>
          {c.cricosCode && (
            <span className="text-[11px] font-mono text-muted-soft">
              · CRICOS {c.cricosCode}
            </span>
          )}
        </div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tighter2 text-ink leading-[1.05] mb-3">
          {c.name}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {c.isPreferred && (
            <span className="badge bg-gold/15 text-navy inline-flex items-center gap-1">
              <IconStarFilled size={12} />
              Preferred
            </span>
          )}
          {isVerified && (
            <span className="badge bg-success-bg text-success inline-flex items-center gap-1">
              <IconShieldCheck size={12} />
              Verified · {formatDate(c.verifiedAt)}
            </span>
          )}
          {isPr && (
            <span className="badge bg-warn-bg text-warn inline-flex items-center gap-1">
              <IconFlag size={12} />
              PR pathway
            </span>
          )}
          {c.providerPrimaryState && (
            <span className="badge bg-cream-deep text-navy inline-flex items-center gap-1">
              <IconMapPin size={12} />
              {c.providerPrimaryState}
            </span>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="mb-6">
        <CourseStatStrip course={c} />
      </div>

      {/* Action buttons — desktop inline row (mobile uses the sticky bar below) */}
      <div className="mb-8 hidden lg:flex flex-wrap gap-3">
        <Button
          variant="primary"
          size="lg"
          onClick={() => setOverviewOpen(true)}
          leftIcon={<IconFileText size={16} />}
        >
          Send overview
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setChecklistOpen(true)}
          leftIcon={<IconListCheck size={16} />}
        >
          Send checklist
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => toggleShortlist.mutate({ courseId: c.id, saved })}
          leftIcon={saved ? <IconStarFilled size={16} className="text-gold" /> : <IconStar size={16} />}
        >
          {saved ? 'Shortlisted' : 'Add to shortlist'}
        </Button>
        {c.providerWebsiteUrl && (
          <a
            href={c.providerWebsiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-navy-soft underline underline-offset-4 ml-auto"
          >
            Provider site
            <IconExternalLink size={14} />
          </a>
        )}
      </div>

      {/* Provider link on mobile (the sticky bar carries the primary actions) */}
      {c.providerWebsiteUrl && (
        <a
          href={c.providerWebsiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="lg:hidden inline-flex items-center gap-1.5 text-sm text-navy underline underline-offset-4 mb-6"
        >
          Provider site
          <IconExternalLink size={14} />
        </a>
      )}

      {/* Agency overlay (master courses only) */}
      {c.source === 'master' && user?.role === 'admin' && (
        <div className="mb-8">
          <AgencyOverlayEditor course={c} />
        </div>
      )}
      {c.source === 'master' && user?.role === 'consultant' && c.agencyNotes && (
        <div className="mb-8">
          <AgencyNotesPanel notes={c.agencyNotes} />
        </div>
      )}

      {/* Content sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {c.courseStructure && (
            <Section title="Course structure">
              <Paragraph>{c.courseStructure}</Paragraph>
            </Section>
          )}

          {c.academicPrerequisites && (
            <Section title="Entry requirements">
              <div className="space-y-3">
                <Paragraph>{c.academicPrerequisites}</Paragraph>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-line">
                  {c.ieltsOverall && (
                    <RequirementCell
                      label="IELTS Academic"
                      value={c.ieltsOverall}
                      detail={c.ieltsRequirementText}
                    />
                  )}
                  {c.pteOverall && (
                    <RequirementCell
                      label="PTE"
                      value={String(c.pteOverall)}
                      detail={c.pteRequirementText}
                    />
                  )}
                  {c.toeflOverall && (
                    <RequirementCell
                      label="TOEFL iBT"
                      value={String(c.toeflOverall)}
                      detail={c.toeflRequirementText}
                    />
                  )}
                </div>
              </div>
            </Section>
          )}

          {c.careerOutcomesSummary && (
            <Section title="Career outcomes">
              <Paragraph>{c.careerOutcomesSummary}</Paragraph>
              {c.anzscoCodes.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.anzscoCodes.map((code) => (
                    <span
                      key={code}
                      className="badge bg-cream-deep text-navy font-mono text-[11px]"
                    >
                      ANZSCO {code}
                    </span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {c.pathways && (
            <Section title="Pathways">
              <Paragraph>{c.pathways}</Paragraph>
            </Section>
          )}
        </div>

        {/* Sidebar column */}
        <div className="space-y-6">
          {c.visaSubclassRelevance.length > 0 && (
            <Section title="Visa subclasses">
              <div className="flex flex-wrap gap-1.5">
                {c.visaSubclassRelevance.map((sc) => (
                  <span
                    key={sc}
                    className="badge bg-info-bg text-info font-mono text-[11px]"
                  >
                    Subclass {sc}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {Object.keys(c.occupationListMemberships).length > 0 && (
            <Section title="Occupation lists">
              <ul className="space-y-1.5 text-sm">
                {Object.entries(c.occupationListMemberships).map(([list, codes]) => (
                  <li key={list} className="flex items-baseline justify-between">
                    <span className="uppercase text-xs tracking-wider text-muted font-medium">
                      {list}
                    </span>
                    <span className="text-ink-soft text-xs">
                      {Array.isArray(codes) && codes.length} role
                      {Array.isArray(codes) && codes.length === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="Fees">
            <dl className="space-y-2 text-sm">
              <FeeRow label="Annual tuition" value={centsToAud(c.annualTuitionCents)} />
              {lifetimeFee && <FeeRow label="Total program" value={lifetimeFee} />}
              {c.oshcAnnualCents !== null && (
                <FeeRow label="OSHC (annual)" value={centsToAud(c.oshcAnnualCents)} />
              )}
              {c.applicationFeeCents !== null && (
                <FeeRow label="Application fee" value={centsToAud(c.applicationFeeCents)} />
              )}
            </dl>
          </Section>

          {c.documentsRequired.length > 0 && (
            <Section title="Documents required">
              <ul className="space-y-1.5 text-sm text-ink-soft">
                {c.documentsRequired.map((d) => (
                  <li key={d} className="flex gap-2">
                    <span className="text-gold mt-0.5">•</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      </div>

      {/* Sticky mobile action bar — primary actions within thumb reach */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex items-center gap-2 border-t border-line bg-cream/95 backdrop-blur px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom,0px))]">
        <Button
          variant="primary"
          onClick={() => setOverviewOpen(true)}
          className="flex-1 h-12 px-2 text-sm"
          leftIcon={<IconFileText size={16} />}
        >
          Overview
        </Button>
        <Button
          variant="secondary"
          onClick={() => setChecklistOpen(true)}
          className="flex-1 h-12 px-2 text-sm"
          leftIcon={<IconListCheck size={16} />}
        >
          Checklist
        </Button>
        <Button
          variant="ghost"
          onClick={() => toggleShortlist.mutate({ courseId: c.id, saved })}
          aria-label={saved ? 'Remove from shortlist' : 'Add to shortlist'}
          className="shrink-0 h-12 w-12 px-0"
        >
          {saved ? (
            <IconStarFilled size={20} className="text-gold" />
          ) : (
            <IconStar size={20} />
          )}
        </Button>
      </div>

      <SendOverviewModal
        open={overviewOpen}
        onClose={() => setOverviewOpen(false)}
        course={{
          id: c.id,
          name: c.name,
          provider_name: c.providerName,
          cricos_code: c.cricosCode ?? null,
        }}
      />
      <SendChecklistModal
        open={checklistOpen}
        onClose={() => setChecklistOpen(false)}
        course={{
          id: c.id,
          name: c.name,
          provider_name: c.providerName,
          cricos_code: c.cricosCode ?? null,
        }}
      />
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="surface p-5">
      <h2 className="font-serif text-lg text-ink tracking-tightish mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-ink-soft leading-relaxed whitespace-pre-line">{children}</p>
  );
}

function RequirementCell({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string | null;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-muted font-semibold">
        {label}
      </div>
      <div className="font-serif text-lg text-ink tracking-tightish">{value}</div>
      {detail && <div className="text-xs text-muted mt-0.5">{detail}</div>}
    </div>
  );
}

function FeeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink-soft font-medium">{value}</dd>
    </div>
  );
}
